import React, { useEffect, useState } from 'react';
import { DialogActions, DialogContent, DialogTitle, Grid, IconButton, Checkbox, ListItemText, MenuItem, FormControl, Select as MuiSelect, OutlinedInput, InputLabel } from '@mui/material';
import { CloseIcon } from '../../helper/Icons';
import { Button, ErrorMessage, Input, Select } from '../../components/Common';
import { useAddNewDiscountMutation, useUpdateDiscountMutation } from '../../services/Discount';
import { useGetserviceDepartmentDropdownQuery } from '../../services/ServiceDepartment';
import { useGetStateListQuery, useGetCityListQuery } from '../../services/Location';
import { useMessage } from '../../components/Common/Message/MessageContext';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const AddDiscount = ({ handleClose, modalOpen }) => {
    const { showMessage } = useMessage();

    const [couponCode, setCouponCode] = useState('');
    const [discountPercentage, setDiscountPercentage] = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedState, setSelectedState] = useState('');
    const [selectedCity, setSelectedCity] = useState('');

    const [couponErrorMessage, setCouponErrorMessage] = useState('');
    const [discountErrorMessage, setDiscountErrorMessage] = useState('');
    const [categoryErrorMessage, setCategoryErrorMessage] = useState('');
    const [cityErrorMessage, setCityErrorMessage] = useState('');

    const [addDiscount] = useAddNewDiscountMutation();
    const [updateDiscount] = useUpdateDiscountMutation();
    const { data: categoryData } = useGetserviceDepartmentDropdownQuery();
    const { data: stateData } = useGetStateListQuery();
    const { data: cityData } = useGetCityListQuery(selectedState, { skip: !selectedState });

    const categories = categoryData?.data || [];
    const states = stateData?.data || [];
    const cities = cityData?.data || [];

    const handleCouponChange = (e) => {
        const value = e.target.value.toUpperCase();
        if (/^[A-Z0-9]*$/.test(value)) {
            setCouponCode(value);
        }
    };

    const handleCategoryChange = (event) => {
        const {
            target: { value },
        } = event;

        if (value.includes('all')) {
            if (selectedCategories.length === categories.length) {
                setSelectedCategories([]);
            } else {
                setSelectedCategories(categories.map((cat) => cat.id));
            }
            return;
        }

        setSelectedCategories(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
        );
    };

    const isAllSelected = categories.length > 0 && selectedCategories.length === categories.length;

    const handleSubmit = async () => {
        setCouponErrorMessage('');
        setDiscountErrorMessage('');
        setCategoryErrorMessage('');
        setCityErrorMessage(''); // Clear city error message

        let hasError = false;
        if (!couponCode) {
            setCouponErrorMessage('Coupon Code is mandatory.');
            hasError = true;
        }
        if (!discountPercentage) {
            setDiscountErrorMessage('Discount Percentage is mandatory.');
            hasError = true;
        } else if (discountPercentage < 0 || discountPercentage > 100) {
            setDiscountErrorMessage('Discount Percentage must be between 0 and 100.');
            hasError = true;
        }

        if (selectedCategories.length === 0) {
            setCategoryErrorMessage('Please select at least one category.');
            hasError = true;
        }

        if (!selectedCity) {
            setCityErrorMessage('Please select a city.');
            hasError = true;
        }

        if (hasError) return;

        const body = {
            couponCode,
            discountPercentage,
            categories: selectedCategories,
            city: selectedCity,
            state: selectedState
        };

        try {
            let response;
            if (modalOpen.isEdit) {
                response = await updateDiscount({ formData: body, id: modalOpen.id });
            } else {
                response = await addDiscount(body);
            }

            const { status, message } = response?.data || {};

            if (status) {
                showMessage('success', message);
                handleClose();
                setCouponCode('');
                setDiscountPercentage('');
                setSelectedCategories([]);
                setSelectedState('');
                setSelectedCity('');
            } else {
                showMessage('error', message);
            }
        } catch (error) {
            showMessage('error', 'An error occurred while processing your request.');
        }
    };

    useEffect(() => {
        if (modalOpen.data) {
            setCouponCode(modalOpen.data.couponCode);
            setDiscountPercentage(modalOpen.data.discountPercentage);
            // Assuming backend returns array of objects for categories
            const catIds = Array.isArray(modalOpen.data.categories)
                ? modalOpen.data.categories.map(c => c.id || c) // Handle if objects or ids
                : [];
            setSelectedCategories(catIds);

            // Handle State: backend returns object { id: 1, state: 'Delhi' } or null
            const stateVal = modalOpen.data.state && typeof modalOpen.data.state === 'object'
                ? modalOpen.data.state.id
                : modalOpen.data.state || ''; // Fallback if it's just an ID or empty
            setSelectedState(stateVal);

            // Handle City: backend returns object { id: 1, city: 'New Delhi' } or null
            const cityVal = modalOpen.data.city && typeof modalOpen.data.city === 'object'
                ? modalOpen.data.city.id
                : modalOpen.data.city || ''; // Fallback if it's just an ID or empty

            setSelectedCity(cityVal);
        }
    }, [modalOpen.data]);

    return (
        <>
            <DialogTitle sx={{ m: 0, p: 2 }} id="Common_modal">
                {modalOpen.isEdit ? 'Edit' : 'Add'} Discount
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
                <Grid container gap={2} width={{ sm: '400px' }} paddingY={1}>
                    <Grid item xs={12}>
                        <Input labelinput='Coupon Code' value={couponCode} onChange={handleCouponChange} />
                        <ErrorMessage message={couponErrorMessage} />
                    </Grid>
                    <Grid item xs={12}>
                        <Input labelinput='Discount %' type="number" value={discountPercentage} onChange={(e) => setDiscountPercentage(e.target.value)} />
                        <ErrorMessage message={discountErrorMessage} />
                    </Grid>
                    <Grid item xs={12}>
                        <Select
                            selectList={states}
                            labelinput="State"
                            name="state"
                            value={selectedState}
                            onChange={(e) => {
                                setSelectedState(e.target.value);
                                setSelectedCity(''); // Reset city when state changes
                            }}
                            placeholder="Select State"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Select
                            selectList={cities}
                            labelinput="City"
                            name="city"
                            value={selectedCity}
                            onChange={(e) => setSelectedCity(e.target.value)}
                            disabled={!selectedState}
                            placeholder="Select City"
                        />
                        <ErrorMessage message={cityErrorMessage} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel id="category-label" sx={{ fontSize: { xs: '12px', sm: '13px' }, fontWeight: 500, mb: '2px', color: 'black' }}>Category</InputLabel>
                        <FormControl fullWidth>
                            <MuiSelect
                                labelId="category-label"
                                multiple
                                displayEmpty
                                value={selectedCategories}
                                onChange={handleCategoryChange}
                                input={<OutlinedInput />}
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return <em>Select Categories</em>;
                                    }
                                    if (selected.length === categories.length) {
                                        return "All Categories";
                                    }
                                    return categories
                                        .filter(cat => selected.includes(cat.id))
                                        .map(cat => cat.serviceName)
                                        .join(', ');
                                }}
                                MenuProps={MenuProps}
                                sx={{ height: '40px' }}
                            >
                                <MenuItem value="all">
                                    <Checkbox checked={isAllSelected} />
                                    <ListItemText primary="Select All" />
                                </MenuItem>
                                {categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        <Checkbox checked={selectedCategories.includes(cat.id)} />
                                        <ListItemText primary={cat.serviceName} />
                                    </MenuItem>
                                ))}
                            </MuiSelect>
                        </FormControl>
                        <ErrorMessage message={categoryErrorMessage} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button buttonName={'Cancel'} size='small' color='white' onClick={handleClose} />
                <Button buttonName={modalOpen.isEdit ? 'Update' : 'Save'} size='small' onClick={handleSubmit} />
            </DialogActions>
        </>
    );
};

export default AddDiscount;
